import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Input,
  Textarea,
  Select,
  Button,
  Field,
  Avatar,
  Combobox,
  Option,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Checkbox,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss12Regular,
  Delete24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Attach24Regular,
  Document24Regular,
} from '@fluentui/react-icons';
import { KanbanCard, KanbanUser, CreateCardRequest, KanbanColumn, KanbanRow } from '../../types/kanban';
import { LicenseCheck } from '../../types/license';
import { AvatarColor } from './avatarUtils';
import { CARD_COLORS } from './types';
import { KanbanServiceV2 } from '../../services/kanbanServiceV2';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    margin: 'auto',
    '@media (max-width: 640px)': {
      width: '100vw !important' as any,
      maxWidth: '100vw !important' as any,
      height: '100vh !important' as any,
      maxHeight: '100vh !important' as any,
      borderRadius: '0 !important' as any,
      top: '0 !important' as any,
      left: '0 !important' as any,
      transform: 'none !important' as any,
    },
  },
  dialogContent: {
    maxHeight: '72vh',
    overflowY: 'auto',
    paddingRight: '12px',
    '@media (max-width: 640px)': {
      maxHeight: 'none',
      flex: '1',
      minHeight: '0',
    },
  },
  mobileCloseButton: {
    display: 'none',
    '@media (max-width: 640px)': {
      display: 'block',
      marginTop: tokens.spacingVerticalL,
      paddingTop: tokens.spacingVerticalM,
    },
  },
  formField: {
    marginBottom: tokens.spacingVerticalM,
  },
  colorPicker: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  colorOption: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusSmall,
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorOptionSelected: {
    border: `3px solid ${tokens.colorBrandBackground}`,
  },
  assignmentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  assignmentPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  assignmentPill: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  dismissButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXXS,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  dateInput: {
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(0.5)',
      cursor: 'pointer',
      opacity: 1,
    },
  },
  commentContainer: {
    '&:hover .comment-actions': {
      opacity: 1,
    },
  },
});

interface EditTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isReadOnly: boolean;
  editingCard: KanbanCard | null;
  setEditingCard: (card: KanbanCard | null) => void;
  newCard: Partial<CreateCardRequest>;
  setNewCard: (card: Partial<CreateCardRequest>) => void;
  selectedAssignees: string[];
  validationErrors: Record<string, string>;
  setValidationErrors: (errors: Record<string, string>) => void;
  availableMembers: KanbanUser[];
  userPhotos: Record<string, string>;
  getAvatarColor: (userId: string) => AvatarColor;
  onAssigneeSelectionChange: (selectedOptions: string[]) => void;
  onRemoveAssignee: (userId: string) => void;
  customUserInput: string;
  setCustomUserInput: (value: string) => void;
  onAddCustomUser: () => void;
  autoSaveCard: (overrides?: Partial<CreateCardRequest>) => void;
  themedScrollbarClass: string;
  // Attachments
  license: LicenseCheck | null;
  openPricingPage: () => void;
  attachmentError: string | null;
  setAttachmentError: (error: string | null) => void;
  onFileUpload: (file: File) => Promise<void>;
  kanbanService: KanbanServiceV2;
  // Checklist
  newChecklistItem: string;
  setNewChecklistItem: (value: string) => void;
  onAddChecklistItem: () => Promise<void>;
  onToggleChecklistItem: (itemId: string) => Promise<void>;
  onDeleteChecklistItem: (itemId: string) => Promise<void>;
  // Comments
  currentUser: KanbanUser | null;
  newComment: string;
  setNewComment: (value: string) => void;
  onAddComment: () => Promise<void>;
  editingCommentId: string | null;
  editingCommentText: string;
  setEditingCommentText: (value: string) => void;
  onEditComment: (commentId: string, currentText: string) => void;
  onSaveEditComment: () => Promise<void>;
  onCancelEditComment: () => void;
  onDeleteComment: (commentId: string) => Promise<void> | void;
  newlyAddedCommentId: string | null;
  onDeleteAttachment: (attachmentId: string) => Promise<void> | void;
  formatCommentDate: (date: Date) => string;
  commentTextareaRef: React.RefObject<HTMLTextAreaElement>;
  commentsContainerRef: React.RefObject<HTMLDivElement>;
  commentRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  // Column/row move (for mobile)
  columns?: KanbanColumn[];
  rows?: KanbanRow[];
  onMoveCard?: (columnId: string, rowId: string) => void;
}

export const EditTaskDialog: React.FC<EditTaskDialogProps> = ({
  isOpen,
  onClose,
  isReadOnly,
  editingCard,
  newCard,
  setNewCard,
  selectedAssignees,
  validationErrors,
  setValidationErrors,
  availableMembers,
  userPhotos,
  getAvatarColor,
  onAssigneeSelectionChange,
  onRemoveAssignee,
  customUserInput,
  setCustomUserInput,
  onAddCustomUser,
  autoSaveCard,
  themedScrollbarClass,
  license,
  openPricingPage,
  attachmentError,
  setAttachmentError,
  onFileUpload,
  kanbanService,
  newChecklistItem,
  setNewChecklistItem,
  onAddChecklistItem,
  onToggleChecklistItem,
  onDeleteChecklistItem,
  currentUser,
  newComment,
  setNewComment,
  onAddComment,
  editingCommentId,
  editingCommentText,
  setEditingCommentText,
  onEditComment,
  onSaveEditComment,
  onCancelEditComment,
  onDeleteComment,
  newlyAddedCommentId,
  onDeleteAttachment,
  formatCommentDate,
  commentTextareaRef,
  commentsContainerRef,
  commentRefs,
  columns,
  rows,
  onMoveCard,
}) => {
  const styles = useStyles();
  const cardColors = CARD_COLORS;
  const [confirmDeleteAttachmentId, setConfirmDeleteAttachmentId] = useState<string | null>(null);
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<string | null>(null);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => {
      if (!data.open && data.type === 'escapeKeyDown') {
        onClose();
      }
    }}>
      <DialogSurface className={styles.dialogSurface}>
        <Button
          appearance="subtle"
          icon={<Dismiss12Regular />}
          onClick={onClose}
          style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1 }}
        />
        <DialogTitle>
          {isReadOnly ? 'View Task' : 'Edit Task'}
        </DialogTitle>
        <DialogBody>
          <DialogContent className={themedScrollbarClass} style={{ maxHeight: '72vh', overflowY: 'auto', paddingRight: '12px' }}>
            {/* Title */}
            <div className={styles.formField}>
              <Field
                label="Title"
                required
                validationState={validationErrors.title ? 'error' : 'none'}
                validationMessage={validationErrors.title}
              >
                <Input
                  value={newCard.title || ''}
                  onChange={(_, data) => {
                    setNewCard({ ...newCard, title: data.value });
                    if (validationErrors.title && data.value.trim()) {
                      setValidationErrors({ ...validationErrors, title: '' });
                    }
                  }}
                  onBlur={() => autoSaveCard()}
                  placeholder="Enter task title"
                  readOnly={isReadOnly}
                  style={isReadOnly ? { cursor: 'default' } : undefined}
                  autoComplete="off"
                />
              </Field>
            </div>

            {/* Description */}
            <div className={styles.formField}>
              <Field label="Description">
                <Textarea
                  value={newCard.description || ''}
                  onChange={(_, data) => setNewCard({ ...newCard, description: data.value })}
                  onBlur={() => autoSaveCard()}
                  placeholder="Enter task description"
                  rows={3}
                  readOnly={isReadOnly}
                  style={isReadOnly ? { cursor: 'default' } : undefined}
                  autoComplete="off"
                />
              </Field>
            </div>

            {/* Priority */}
            <div className={styles.formField}>
              <Field label="Priority">
                <Select
                  value={newCard.priority || 'medium'}
                  onChange={(_, data) => {
                    const newPriority = data.value as 'low' | 'medium' | 'high';
                    setNewCard({ ...newCard, priority: newPriority });
                    setTimeout(() => autoSaveCard({ priority: newPriority }), 0);
                  }}
                  disabled={isReadOnly}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </Select>
              </Field>
            </div>

            {/* Column / Row (move card) */}
            {columns && rows && onMoveCard && editingCard && !isReadOnly && (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className={styles.formField} style={{ flex: 1 }}>
                  <Field label="Column">
                    <Select
                      value={editingCard.columnId}
                      onChange={(_, data) => {
                        onMoveCard(data.value, editingCard.rowId);
                      }}
                    >
                      {columns.map(col => (
                        <option key={col.id} value={col.id}>{col.title}</option>
                      ))}
                    </Select>
                  </Field>
                </div>
                {rows.length > 1 && (
                  <div className={styles.formField} style={{ flex: 1 }}>
                    <Field label="Row">
                      <Select
                        value={editingCard.rowId}
                        onChange={(_, data) => {
                          onMoveCard(editingCard.columnId, data.value);
                        }}
                      >
                        {rows.map(row => (
                          <option key={row.id} value={row.id}>{row.name}</option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}
              </div>
            )}

            {/* Task Color */}
            <div className={styles.formField}>
              <Field label="Task Color">
                <div className={styles.colorPicker} style={isReadOnly ? { pointerEvents: 'none' } : undefined}>
                  {cardColors.map((color) => (
                    <div
                      key={color.value || 'none'}
                      className={`${styles.colorOption} ${newCard.color === color.value ? styles.colorOptionSelected : ''}`}
                      style={{
                        backgroundColor: color.value || tokens.colorNeutralBackground3,
                        border: color.value ? undefined : `2px dashed ${tokens.colorNeutralStroke2}`
                      }}
                      onClick={() => {
                        if (isReadOnly) return;
                        setNewCard({ ...newCard, color: color.value });
                        setTimeout(() => autoSaveCard({ color: color.value }), 0);
                      }}
                      title={color.label}
                    >
                      {!color.value && <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>&#10005;</span>}
                    </div>
                  ))}
                </div>
              </Field>
            </div>

            {/* Dates */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className={styles.formField} style={{ flex: 1 }}>
                <Field label="Start Date">
                  <Input
                    type="date"
                    value={newCard.startDate || ''}
                    data-has-value={newCard.startDate ? 'true' : 'false'}
                    onChange={(_, data) => {
                      setNewCard({ ...newCard, startDate: data.value });
                      if (validationErrors.endDate) {
                        setValidationErrors({ ...validationErrors, endDate: '' });
                      }
                    }}
                    onBlur={() => autoSaveCard()}
                    className={styles.dateInput}
                    readOnly={isReadOnly}
                    style={isReadOnly ? { cursor: 'default' } : undefined}
                    autoComplete="off"
                  />
                </Field>
              </div>
              <div className={styles.formField} style={{ flex: 1 }}>
                <Field
                  label="End Date"
                  validationState={validationErrors.endDate ? 'error' : 'none'}
                  validationMessage={validationErrors.endDate}
                >
                  <Input
                    type="date"
                    value={newCard.endDate || ''}
                    data-has-value={newCard.endDate ? 'true' : 'false'}
                    onChange={(_, data) => {
                      setNewCard({ ...newCard, endDate: data.value });
                      if (validationErrors.endDate) {
                        setValidationErrors({ ...validationErrors, endDate: '' });
                      }
                    }}
                    onBlur={() => autoSaveCard()}
                    className={styles.dateInput}
                    readOnly={isReadOnly}
                    style={isReadOnly ? { cursor: 'default' } : undefined}
                    autoComplete="off"
                  />
                </Field>
              </div>
            </div>

            {/* Assignees */}
            <div className={styles.formField}>
              <Field label="Assign to">
                <div className={styles.assignmentSection}>
                  {selectedAssignees.length > 0 && (
                    <div className={styles.assignmentPills}>
                      {selectedAssignees.map((userId) => {
                        const member = availableMembers.find(m => m.id === userId);
                        return member ? (
                          <div key={userId} className={styles.assignmentPill}>
                            <Avatar
                              image={userPhotos[userId] ? { src: userPhotos[userId] } : undefined}
                              name={member.displayName}
                              color={getAvatarColor(userId)}
                              size={24}
                            />
                            <Text size={200}>{member.displayName}</Text>
                            {!isReadOnly && (
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<Dismiss12Regular />}
                                onClick={() => onRemoveAssignee(userId)}
                                className={styles.dismissButton}
                              />
                            )}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  {!isReadOnly && (
                    <>
                      <Combobox
                        multiselect
                        placeholder={selectedAssignees.length > 0 ? "Add more members" : "Select team members"}
                        selectedOptions={selectedAssignees}
                        onOptionSelect={(_, data) => {
                          if (data.selectedOptions) {
                            onAssigneeSelectionChange(data.selectedOptions);
                          }
                        }}
                        autoComplete="off"
                      >
                        {availableMembers.map((member) => (
                          <Option key={member.id} value={member.id} text={member.displayName}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Avatar
                                image={userPhotos[member.id] ? { src: userPhotos[member.id] } : undefined}
                                name={member.displayName}
                                color={getAvatarColor(member.id)}
                                size={24}
                              />
                              <Text>{member.displayName}</Text>
                            </div>
                          </Option>
                        ))}
                      </Combobox>
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                        <Input
                          placeholder="Or type a custom name..."
                          value={customUserInput}
                          onChange={(_, data) => setCustomUserInput(data.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              onAddCustomUser();
                            }
                          }}
                          autoComplete="off"
                          style={{ flex: 1 }}
                        />
                        <Button
                          appearance="secondary"
                          size="small"
                          onClick={onAddCustomUser}
                          disabled={!customUserInput.trim()}
                        >
                          Add
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Field>
            </div>

            {/* Attachments Section */}
            <div className={styles.formField} style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
              <Field label="Attachments">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {editingCard?.attachments && editingCard.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {editingCard.attachments.map((attachment: any) => (
                        <div
                          key={attachment.id}
                          style={{
                            backgroundColor: tokens.colorNeutralBackground2,
                            padding: '0.75rem',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.75rem'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                            <Document24Regular />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: 0 }}>
                              <Text weight="semibold" size={300} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {attachment.name}
                              </Text>
                              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                                {(attachment.size / 1024).toFixed(1)} KB - {new Date(attachment.uploadedAt).toLocaleDateString()}
                              </Text>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <Button
                              appearance="subtle"
                              size="small"
                              onClick={() => kanbanService.downloadAttachment(attachment)}
                            >
                              Download
                            </Button>
                            {!isReadOnly && (
                              confirmDeleteAttachmentId === attachment.id ? (
                                <>
                                  <Button
                                    appearance="primary"
                                    size="small"
                                    onClick={() => {
                                      onDeleteAttachment(attachment.id);
                                      setConfirmDeleteAttachmentId(null);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                  <Button
                                    appearance="subtle"
                                    size="small"
                                    onClick={() => setConfirmDeleteAttachmentId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={<Delete24Regular />}
                                  onClick={() => setConfirmDeleteAttachmentId(attachment.id)}
                                />
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isReadOnly && (!license || license.restrictions.canUploadFiles) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {attachmentError && (
                        <MessageBar intent="error">
                          <MessageBarBody>{attachmentError}</MessageBarBody>
                          <MessageBarActions>
                            <Button
                              appearance="transparent"
                              icon={<Dismiss12Regular />}
                              onClick={() => setAttachmentError(null)}
                            />
                          </MessageBarActions>
                        </MessageBar>
                      )}
                      <input
                        type="file"
                        id="file-upload"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            await onFileUpload(file);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button
                        appearance="subtle"
                        icon={<Attach24Regular />}
                        onClick={() => document.getElementById('file-upload')?.click()}
                      >
                        Attach File
                      </Button>
                    </div>
                  )}

                  {!isReadOnly && license && !license.restrictions.canUploadFiles && (
                    <div
                      onClick={(license.plan === 'free' || license.isExpired) ? openPricingPage : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: (license.plan === 'free' || license.isExpired) ? 'pointer' : 'default',
                        padding: '8px 12px',
                        backgroundColor: tokens.colorNeutralBackground3,
                        borderRadius: '6px',
                      }}
                    >
                      <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                        File attachments require a paid plan.
                      </Text>
                      <Button
                        appearance="primary"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); openPricingPage(); }}
                      >
                        Get License
                      </Button>
                    </div>
                  )}
                </div>
              </Field>
            </div>

            {/* Checklist Section */}
            <div className={styles.formField} style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
              <Field label="Checklist">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {editingCard?.checklist && editingCard.checklist.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                          {editingCard.checklist.filter((item: any) => item.completed).length} of {editingCard.checklist.length} completed
                        </Text>
                        <Text size={200} weight="semibold" style={{
                          color: editingCard.checklist.every((item: any) => item.completed)
                            ? tokens.colorPaletteGreenForeground1
                            : tokens.colorNeutralForeground2
                        }}>
                          {Math.round((editingCard.checklist.filter((item: any) => item.completed).length / editingCard.checklist.length) * 100)}%
                        </Text>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: tokens.colorNeutralBackground3,
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${(editingCard.checklist.filter((item: any) => item.completed).length / editingCard.checklist.length) * 100}%`,
                          height: '100%',
                          backgroundColor: editingCard.checklist.every((item: any) => item.completed)
                            ? tokens.colorPaletteGreenBackground2
                            : tokens.colorBrandBackground,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  )}

                  {editingCard?.checklist && editingCard.checklist.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}>
                      {editingCard.checklist.map((item: any) => (
                        <div
                          key={item.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            backgroundColor: item.completed ? tokens.colorNeutralBackground1 : tokens.colorNeutralBackground2,
                            border: `1px solid ${item.completed ? tokens.colorNeutralStroke2 : 'transparent'}`,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                          }}
                        >
                          <Checkbox
                            checked={item.completed}
                            onChange={() => !isReadOnly && onToggleChecklistItem(item.id)}
                          />
                          <Text
                            style={{
                              flex: 1,
                              textDecoration: item.completed ? 'line-through' : 'none',
                              color: item.completed ? tokens.colorNeutralForeground3 : tokens.colorNeutralForeground1,
                              fontSize: '14px',
                            }}
                          >
                            {item.text}
                          </Text>
                          {!isReadOnly && (
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Delete24Regular />}
                              onClick={() => onDeleteChecklistItem(item.id)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!isReadOnly && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <Input
                        placeholder="Add checklist item..."
                        value={newChecklistItem}
                        onChange={(_, data) => setNewChecklistItem(data.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newChecklistItem.trim()) {
                            onAddChecklistItem();
                          }
                        }}
                        style={{ flex: 1 }}
                        autoComplete="off"
                      />
                      <Button
                        appearance="primary"
                        disabled={!newChecklistItem.trim()}
                        onClick={onAddChecklistItem}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </Field>
            </div>

            {/* Comments Section */}
            <div className={styles.formField} style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
              <Field label="Comments">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {!isReadOnly && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <Textarea
                        ref={commentTextareaRef as any}
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        style={{ width: '100%', minHeight: '60px' }}
                        resize="vertical"
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          appearance="primary"
                          onClick={onAddComment}
                          disabled={!newComment.trim()}
                        >
                          Comment
                        </Button>
                      </div>
                    </div>
                  )}

                  {editingCard?.comments && editingCard.comments.length > 0 && (
                    <div ref={commentsContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {[...editingCard.comments].reverse().map((comment: any) => (
                        <div
                          key={comment.id}
                          ref={(el) => { commentRefs.current[comment.id] = el; }}
                          className={styles.commentContainer}
                          style={{
                            backgroundColor: newlyAddedCommentId === comment.id ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground2,
                            padding: '0.75rem',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            boxShadow: newlyAddedCommentId === comment.id ? tokens.shadow8 : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Avatar
                                name={comment.author.displayName}
                                image={userPhotos[comment.author.id] ? { src: userPhotos[comment.author.id] } : undefined}
                                color={getAvatarColor(comment.author.id)}
                                size={24}
                              />
                              <Text size={300} weight="semibold">{comment.author.displayName}</Text>
                              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                                {formatCommentDate(comment.createdAt)}
                                {comment.updatedAt && comment.updatedAt.getTime() !== comment.createdAt.getTime() && ' (edited)'}
                              </Text>
                            </div>
                            {!isReadOnly && currentUser && comment.author.id === currentUser.id && (
                              confirmDeleteCommentId === comment.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Text size={200}>Delete?</Text>
                                  <Button
                                    appearance="primary"
                                    size="small"
                                    onClick={() => {
                                      onDeleteComment(comment.id);
                                      setConfirmDeleteCommentId(null);
                                    }}
                                  >
                                    Yes
                                  </Button>
                                  <Button
                                    appearance="subtle"
                                    size="small"
                                    onClick={() => setConfirmDeleteCommentId(null)}
                                  >
                                    No
                                  </Button>
                                </div>
                              ) : (
                                <Menu>
                                  <MenuTrigger disableButtonEnhancement>
                                    <Button
                                      appearance="subtle"
                                      size="small"
                                      icon={<MoreHorizontal24Regular />}
                                    />
                                  </MenuTrigger>
                                  <MenuPopover>
                                    <MenuList>
                                      <MenuItem
                                        icon={<Edit24Regular />}
                                        onClick={() => onEditComment(comment.id, comment.text)}
                                      >
                                        Edit comment
                                      </MenuItem>
                                      <MenuItem
                                        icon={<Delete24Regular />}
                                        onClick={() => setConfirmDeleteCommentId(comment.id)}
                                      >
                                        Delete comment
                                      </MenuItem>
                                    </MenuList>
                                  </MenuPopover>
                                </Menu>
                              )
                            )}
                          </div>
                          {editingCommentId === comment.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <Textarea
                                value={editingCommentText}
                                onChange={(e) => setEditingCommentText(e.target.value)}
                                style={{ width: '100%', minHeight: '60px' }}
                                resize="vertical"
                              />
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                <Button
                                  appearance="secondary"
                                  size="small"
                                  onClick={onCancelEditComment}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  appearance="primary"
                                  size="small"
                                  onClick={onSaveEditComment}
                                  disabled={!editingCommentText.trim()}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Text size={300} style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</Text>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
            </div>
            <div className={styles.mobileCloseButton}>
              <Button
                appearance="secondary"
                onClick={onClose}
                style={{ width: '100%' }}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
